/// <amd-module name="Wit/ClientWorkItemService/IClientWorkItemService" />
import { TeamContext } from "Tfs/Platform/Contracts/Core";
import { getTFSProject } from "Tfs/Platform/Page";
import { IVssObservableService, IVssPageContext, VssObservableService } from "VSS/Platform/Context";
import { ParentChildWIMap, ReorderResult } from "Wit/Clients/Work/Contracts/Work";
import { getWorkClient } from "Wit/Clients/Work/RestClient/Work";
import { SerialPromiseQueue } from "Wit/ClientWorkItemService/SerialPromiseQueue";
import { CoreFieldRefNames } from "Wit/Common/Generated/Constants";
import { IWorkItem } from "Wit/WorkItemModel/OM/WorkItemInterfaces";
import { IWorkItemModelService } from "Wit/WorkItemModel/Services/IWorkItemModelService";
import { IWorkItemSavingService } from "Wit/WorkItemModel/Services/WorkItemSavingService";
import { WorkItemLink } from "Wit/WorkItemModel/WorkItem/Links";
import { FieldRefNames, IClientWorkItem, IObservableClientWorkItem, ObservedWorkItem, ParentField, WorkItemEvents } from "./IClientWorkItem";

export const enum ClientWorkItemServiceEvent {
    NewWorkItemAdded = "NewWorkItemAdded",
    WorkItemUpdated = "WorkItemUpdated",
    PartialWorkItemUpdated = "PartialWorkItemUpdated",
    WorkItemRemoved = "WorkItemRemoved"
}

export interface IReorderOperation {
    ids: number[];
    iterationId?: string;
    iterationPath?: string;
    nextId?: number;
    onFailure?: (e: any) => void;
    onSuccess?: (reorderResult: ReorderResult[]) => void;
    parentId?: number;
    previousId?: number;
    teamId: string;
    projectId: string;
}

export type ClientWorkItemServiceEventUnion = keyof typeof ClientWorkItemServiceEvent;

export interface IClientWorkItemUpdatedEvent<TWorkItem extends IClientWorkItem> {
    item?: TWorkItem;
}

export interface IReorderInIterationOptions {
    /**
     * If false, we reorder the items locally, but do not attempt to persist the change to the server. Defaults to true.
     */
    saveToServer?: boolean;
    /**
     * If true, we save the reorder to the server even if the items appear to already be in order. Defaults to false.
     *
     * Handy after calling with saveToServer set to false.
     */
    saveToServerEvenIfAlreadySorted?: boolean;
    onFailure?: (e: any) => void;
    onSuccess?: (reorderResult: ReorderResult[]) => void;
}

export type TopBottom = "top" | "bottom";

export interface IClientWorkItemService<
    TObservableWorkItem extends IObservableClientWorkItem<TWorkItem>,
    TWorkItem extends IClientWorkItem = ObservedWorkItem<TObservableWorkItem>
> extends IVssObservableService<IClientWorkItemUpdatedEvent<TWorkItem>, ClientWorkItemServiceEventUnion> {
    /**
     * Get a work item. This will not fetch an item from the server, if it has not been previously added to the store
     * this will return undefined.
     * @param id
     */
    get(id: number | IWorkItem): TObservableWorkItem | undefined;
    /**
     * Get all work items in the store
     */
    getAllItems(): TObservableWorkItem[];
    /**
     * Add a work item to the store.
     * @param workItem
     */
    add(workItem: TWorkItem): TObservableWorkItem;
    /**
     * Remove a work item from the store
     */
    clear(workItemId: number): void;
    /**
     * Remove all work items from the store
     */
    clearAll(): void;
    /**
     * Return all ObservableItems registered in the service
     */
    getAllItems(): TObservableWorkItem[];
    /**
     *
     * @param teamId
     * @param backlogReferenceName
     */
    populateWorkItemParents(teamId: string, backlogReferenceName: string, ids?: number[]): Promise<void>;
    /**
     * Queue a reorder operation - requests are processed sequentially from oldest to newest
     */
    queueReorderOperation(operation: IReorderOperation): void;
    /**
     * Place an item within an iteration
     * @param item the item to move
     * @param iteration
     * @param team
     * @param previousItem the item to appear before this one
     * @param nextItem the item to appear after this one
     */
    reorderWorkItems(
        itemId: number,
        projectId: string,
        teamId: string,
        previousId?: number,
        nextId?: number,
        iterationPath?: string,
        options?: IReorderInIterationOptions,
        orderFieldRefName?: string,
        updateStackRank?: boolean,
        iterationId?: string,
        parentId?: number
    ): Promise<void>;
    /**
     * Reparent a list of work items
     * @param workItemIds ids of items to reparent
     * @param newParent id of the work item to be parented to
     */
    reparentWorkItems(workItemIds: number[], newParent: number): Promise<void>;
    /**
     * If the item with parentId has children, update those children to have the correct parent title.
     */
    updateParentFieldTitle(parentId: number, parentTitle?: string, parentDeleted?: boolean): void;
}

/**
 * Base class work item service that handles client work items
 */
export abstract class ClientWorkItemService<
        TObservableWorkItem extends IObservableClientWorkItem<TWorkItem>,
        TWorkItem extends IClientWorkItem = ObservedWorkItem<TObservableWorkItem>
    >
    extends VssObservableService<IClientWorkItemUpdatedEvent<TWorkItem>, ClientWorkItemServiceEventUnion>
    implements IClientWorkItemService<TObservableWorkItem, TWorkItem>
{
    protected _items = new Map<number, TObservableWorkItem>();
    /** Handler to be called after a new item is saved */
    protected _afterNextSave = new Map<TWorkItem, () => void>();
    /** Reorder requests must be processed sequentially */
    private spq = new SerialPromiseQueue();

    public _serviceStart = (pageContext: IVssPageContext): void => {
        super._serviceStart(pageContext);
    };

    public _serviceEnd = (pageContext: IVssPageContext): void => {
        super._serviceEnd(pageContext);

        this.clearAll();
    };

    public abstract createObservableWorkItem(workItem: TWorkItem): TObservableWorkItem;

    protected _workItemSubscriber = (workItem: TWorkItem, action: WorkItemEvents) => {
        if (action === WorkItemEvents.Set || action === WorkItemEvents.SaveComplete) {
            if (this._afterNextSave.has(workItem) && workItem.iWorkItem.value?.isValid() && !workItem.iWorkItem.value?.isDirty()) {
                const handler = this._afterNextSave.get(workItem)!;
                this._afterNextSave.delete(workItem);
                handler();
            }
        }
        if (action === WorkItemEvents.Set) {
            this._notify({ item: workItem }, ClientWorkItemServiceEvent.WorkItemUpdated);
        }
    };

    public get(idOrItem: number | IWorkItem): TObservableWorkItem | undefined {
        if (typeof idOrItem === "number") {
            return this._items.get(idOrItem);
        }
        const item = idOrItem;
        if (this._items.has(item.id)) {
            return this._items.get(item.id);
        }
        if (this._items.has(item.tempId)) {
            const clientItem = this._items.get(item.tempId)!;
            // the item might have recently gotten an id after being saved and we can now add it to the map
            if (item.id) {
                this._items.set(item.id, clientItem);
                this._items.delete(item.tempId);
            }
            return clientItem;
        }
        return undefined;
    }

    public getAllItems(): TObservableWorkItem[] {
        return Array.from(this._items.values());
    }

    public add(workItem: TWorkItem): TObservableWorkItem {
        const item = this._items.get(workItem.id);
        if (item) {
            item.value = workItem;
        } else {
            const observable = this.createObservableWorkItem(workItem);
            this._items.set(workItem.id, observable);
            observable.subscribe(this._workItemSubscriber);
        }

        return this._items.get(workItem.id)!;
    }

    public clear(workItemId: number): void {
        const item = this.get(workItemId);
        item?.unsubscribe(this._workItemSubscriber);
        this._items.delete(workItemId);
    }

    public clearAll(): void {
        for (const i of this._items.values()) {
            i.unsubscribe(this._workItemSubscriber);
        }

        this._items.clear();
    }

    public async populateWorkItemParents(teamId: string, backlogReferenceName: string, ids?: number[]): Promise<void> {
        const projectId = getTFSProject(this.pageContext)?.id ?? "";
        if (!ids) {
            ids = [...this._items.keys()];
        }

        if (!ids.length) {
            return;
        }

        const parentMappings = await this.getBatchedBoardMappingParentItems({ teamId, projectId } as TeamContext, backlogReferenceName, ids);
        const childToParentMap = getChildToParentMap(parentMappings);
        for (const [childId, parentData] of childToParentMap) {
            const workItem = this.get(childId);
            workItem?.value.setFieldValue(ParentField.Title, parentData.name);
            workItem?.value.setFieldValue(CoreFieldRefNames.Parent, parentData.id);
            workItem?.value.setFieldValue(ParentField.WorkItemType, parentData.workItemTypeName);
            workItem?.notify();
        }
    }

    public queueReorderOperation(operation: IReorderOperation): void {
        this.spq.queue(async () => {
            try {
                const result = await this.runReorderOperation(operation);
                operation.onSuccess?.(result);
            } catch (e: unknown) {
                operation.onFailure?.(e);
            }
        });
    }

    private async getBatchedBoardMappingParentItems(
        teamContext: TeamContext,
        backlogReferenceName: string,
        workItemIds: number[]
    ): Promise<ParentChildWIMap[]> {
        const totalNumberOfChildrenToFetch: number = workItemIds.length;
        const parentItemsPromises: Promise<ParentChildWIMap[]>[] = [];
        const workClient = getWorkClient(this.pageContext);
        for (let i = 0; i < totalNumberOfChildrenToFetch; i += parentMappingBatchSize) {
            const idsToPage = workItemIds.slice(i, i + parentMappingBatchSize);
            parentItemsPromises.push(workClient.getBoardMappingParentItems(teamContext, backlogReferenceName, idsToPage));
        }

        const parentChildMaps = await Promise.all(parentItemsPromises);
        return parentChildMaps.flat();
    }

    private async runReorderOperation(operation: IReorderOperation): Promise<ReorderResult[]> {
        const client = getWorkClient(this.pageContext);
        if (operation.iterationPath) {
            return await client.reorderIterationWorkItems(
                {
                    ids: operation.ids,
                    iterationPath: operation.iterationPath,
                    nextId: operation.nextId ?? 0,
                    previousId: operation.previousId ?? 0,
                    parentId: operation.parentId ?? 0
                },
                {
                    projectId: operation.projectId,
                    teamId: operation.teamId
                } as TeamContext,
                operation.iterationId ?? operation.iterationPath
            );
        } else {
            return await client.reorderBacklogWorkItems(
                {
                    ids: operation.ids,
                    iterationPath: "",
                    nextId: operation.nextId ?? 0,
                    previousId: operation.previousId ?? 0,
                    parentId: operation.parentId ?? 0
                },
                {
                    projectId: operation.projectId,
                    teamId: operation.teamId
                } as TeamContext
            );
        }
    }

    public async reorderWorkItems(
        itemId: number,
        projectId: string,
        teamId: string,
        previousId?: number,
        nextId?: number,
        iterationPath?: string,
        options?: IReorderInIterationOptions,
        orderFieldName: string = FieldRefNames.StackRank,
        updateStackRank?: boolean,
        iterationId?: string,
        parentId?: number
    ): Promise<void> {
        const saveToServer = !options || options.saveToServer !== false;
        const saveToServerEvenIfAlreadySorted = !!(options && options.saveToServerEvenIfAlreadySorted);
        const observableItem = this.get(itemId);
        const item = observableItem?.value;
        if (!item) {
            return;
        }

        // sort order is items with StackRank by StackRank, followed by items without StackRank by deliveryPlansRank
        const previousItem = previousId ? this.get(previousId)?.value : undefined;
        const nextItem = nextId ? this.get(nextId)?.value : undefined;
        let prevRank = previousItem?.getFieldValue<number>(orderFieldName);
        let nextRank = nextItem?.getFieldValue<number>(orderFieldName);
        const currentRank = item.getFieldValue<number>(orderFieldName);
        prevRank = prevRank === undefined ? null : prevRank;
        nextRank = nextRank === undefined ? null : nextRank;
        const sorted =
            currentRank !== null &&
            currentRank !== undefined &&
            (!previousItem || (prevRank !== null && currentRank > prevRank)) &&
            (!nextItem || (nextRank !== null && currentRank < nextRank));
        if (sorted && !saveToServerEvenIfAlreadySorted) {
            return;
        }

        // set item sort order so that the change is reflected locally while the network save is pending
        if (!sorted) {
            if (nextRank !== null) {
                // if the next item has a StackRank we have to use StackRank to rank above it
                if (prevRank !== null) {
                    item.setFieldValue(orderFieldName, (nextRank + prevRank) / 2);
                } else {
                    item.setFieldValue(orderFieldName, nextRank / 2);
                }
            } else if (prevRank !== null) {
                // next item doesn't have StackRank (or doesn't exist), but previous item does
                // put item at the end of the StackRanked items
                item.setFieldValue(orderFieldName, prevRank * 2);
            } else {
                // previous item doesn't have StackRank or doesn't exist and next item doesn't have StackRank or doesn't exist
                // order by clientSideRank
                item.clientSideRank = ((previousItem?.clientSideRank || 0) + (nextItem?.clientSideRank || Number.MAX_SAFE_INTEGER)) / 2;
                item.setFieldValue(orderFieldName, null);
            }
        }

        if (saveToServer) {
            item.saving = true;
            observableItem.notify();

            const onFailure = (ex: any) => {
                item.saving = false;
                observableItem.notify();

                // As reordering works via direct API calls, error field in the work item must be updated
                item.iWorkItem.value?.setError(ex);
                options?.onFailure?.(ex);
            };

            const onSuccess = async (result: ReorderResult[]) => {
                if (result) {
                    for (const r of result) {
                        const item = this.get(r.id);
                        if (!item) {
                            continue;
                        }

                        if (updateStackRank) {
                            // necessary to update StackRank to keep backwards compat for Delivery Plans
                            const currentStackRank = item.value.getFieldValue(orderFieldName);
                            if (currentStackRank !== r.order) {
                                item.value.setFieldValue(orderFieldName, r.order);

                                // updating stack rank via API causes a mismatch between client and server revisions
                                if (item.value) {
                                    const rev = item.value.getRequiredFieldValue<number>(CoreFieldRefNames.Rev);
                                    item.value.setFieldValue(CoreFieldRefNames.Rev, rev + 1);
                                }

                                item.notify();
                            }
                        } else {
                            await item.value.refresh(this.pageContext);
                        }
                    }
                }

                item.saving = false;
                observableItem.notify();

                options?.onSuccess?.(result);
            };

            // queue the reorder operation
            this.queueReorderOperation({
                ids: [itemId],
                nextId,
                previousId,
                projectId,
                teamId,
                iterationPath,
                onFailure,
                onSuccess,
                parentId,
                iterationId
            });
        }
    }

    public async reparentWorkItems(workItemIds: number[], newParent: number): Promise<void> {
        const workItemModelService = this.pageContext.getService<IWorkItemModelService>("IWorkItemModelService");
        const workItems: IWorkItem[] = await workItemModelService.getWorkItems(workItemIds);

        await Promise.all(
            workItems.map(async workItem => {
                const existingParentLink = workItem.getLinks().find(link => link.linkData.LinkType === parentLinkType);
                if (existingParentLink) {
                    workItem.removeLinks([existingParentLink]);
                }

                workItem.addLinks([await WorkItemLink.create(this.pageContext, reverseHierarchyLinkName, newParent)]);
            })
        );

        const savingService = this.pageContext.getService<IWorkItemSavingService>("IWorkItemSavingService");
        await savingService.saveWorkItemsBatchAsync(workItems);
    }

    public updateParentFieldTitle(parentId: number, parentTitle?: string, parentDeleted?: boolean) {
        this._items.forEach((item: TObservableWorkItem, key: number) => {
            // if there is a work item which has the work item being saved as the parent, update the parent title for those work items (if the title has changed)
            if (item.value.getFieldValue<number>(CoreFieldRefNames.Parent) === parentId) {
                if (parentDeleted) {
                    item.value.setFieldValue(ParentField.Title, undefined);
                    item.value.setFieldValue(CoreFieldRefNames.Parent, undefined);
                    item.notify();

                    // refresh to update link data if the form is opened again
                    item.value.refresh(this.pageContext);
                } else {
                    const newTitle = parentTitle;
                    const currentTitle = item.value.getFieldValue(ParentField.Title);
                    if ((currentTitle || newTitle) && currentTitle !== newTitle) {
                        item.value.setFieldValue(ParentField.Title, newTitle);
                        item.notify();
                    }
                }
            }
        });
    }
}

function getChildToParentMap(parentMaps: ParentChildWIMap[]): Map<number, { name: string; id: number; workItemTypeName: string }> {
    const childToParentMap = new Map<number, { name: string; id: number; workItemTypeName: string }>();
    for (const parentMap of parentMaps) {
        for (const childId of parentMap.childWorkItemIds) {
            childToParentMap.set(childId, { name: parentMap.title, id: parentMap.id, workItemTypeName: parentMap.workItemTypeName });
        }
    }

    return childToParentMap;
}

const parentLinkType = -2;
const reverseHierarchyLinkName = "System.LinkTypes.Hierarchy-Reverse";
const parentMappingBatchSize = 200;
